import React from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import { AuthLayout } from "../layouts/AuthLayout";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { ProtectedRoute } from "../components/ProtectedRoute";

// Auth Pages
import { LoginPage } from "../pages/auth/LoginPage";
import { SignupPage } from "../pages/auth/SignupPage";
import { ForgotPasswordPage } from "../pages/auth/ForgotPasswordPage";

// Admin Pages
import { AdminDashboardPage } from "../pages/admin/AdminDashboardPage";
import { PharmaciesPage } from "../pages/admin/PharmaciesPage";
import { PharmacyDetailsPage } from "../pages/admin/PharmacyDetailsPage";
import { DriversPage } from "../pages/admin/DriversPage";
import OrderProcessingPage from "../pages/admin/OrderProcessingPage";
import { OrderDetailPage } from "../pages/admin/OrderDetailPage";
import { RouteMapPage } from "../pages/admin/RouteMapPage";
import { PickupOrdersAdminPage } from "../pages/admin/PickupOrdersAdminPage";
import { AnalyticsPage } from "../pages/admin/AnalyticsPage";
import { SystemPage } from "../pages/admin/SystemPage";
import { AssignRoutesPage } from "../pages/admin/AssignRoutesPage";
import { BarCodePage } from "../pages/admin/BarCodePage";
import AdminTrackingPage from "../pages/admin/TrackingPage";
import AdminRouteTrackingPage from "../pages/admin/RouteTrackingPage";
import { DemoRequestsPage } from "../pages/admin/DemoRequestsPage";

// Pharmacy Pages
import { PharmacyDashboardPage } from "../pages/pharmacy/PharmacyDashboardPage";
import { OrderManagementPage } from "../pages/pharmacy/OrderManagementPage";
import { PickupPage } from "../pages/pharmacy/PickupPage";
// import { DeliveryPage } from "../pages/pharmacy/DeliveryPage";
import { PharmacyAnalyticsPage } from "../pages/pharmacy/PharmacyAnalyticsPage";
import PharmacyTrackingPage from "../pages/pharmacy/TrackingPage";
import PharmacyRouteTrackingPage from "../pages/pharmacy/RouteTrackingPage";

// Shared Pages
import { SettingsPage } from "../pages/dashboard/SettingsPage";
import { NotFoundPage } from "../pages/NotFoundPage";
import { ErrorBoundary } from "../components/ErrorBoundary";

// Public Pages
import SignaturePadPage from "../pages/SignaturePadPage";
import { LandingPage } from "@/components/LandingPage";
import DemoRequestPage from "../pages/DemoRequestPage";

const router = createBrowserRouter([
  {
    path: "/",
    element: <LandingPage />,
    errorElement: <ErrorBoundary />,
  },
  // {
  //   path: "/",
  //   element: <Navigate to="/auth/forgot-password" replace />,
  //   errorElement: <ErrorBoundary />,
  // },
  {
    path: "/auth",
    element: <AuthLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to="login" replace />,
      },
      {
        path: "login",
        element: <LoginPage />,
      },
      {
        path: "signup",
        element: <SignupPage />,
      },
      {
        path: "forgot-password",
        element: <ForgotPasswordPage />,
      },
    ],
  },
  // Admin Routes - Completely separate from pharmacy routes
  {
    path: "/admin",
    element: (
      <ProtectedRoute requireSuperAdmin>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to="dashboard" replace />,
      },
      {
        path: "dashboard",
        element: <AdminDashboardPage />,
      },
      {
        path: "pharmacies",
        element: <PharmaciesPage />,
      },
      {
        path: "pharmacies/:pharmacyId",
        element: <PharmacyDetailsPage />,
      },
      {
        path: "drivers",
        element: <DriversPage />,
      },
      {
        path: "order-processing",
        element: <OrderProcessingPage />,
      },
      {
        path: "orders/:orderId",
        element: <OrderDetailPage />,
      },
      {
        path: "route-management",
        element: <RouteMapPage />, // show map page instead of old management
      },
      {
        path: "route-map",
        element: <RouteMapPage />,
      },
      {
        path: "assign-routes",
        element: <AssignRoutesPage />,
      },
      {
        path: "pickup-orders",
        element: <PickupOrdersAdminPage />,
      },
      // {
      //   path: "tracking",
      //   element: <AdminTrackingPage />,
      // },
      {
        path: "route-tracking",
        element: <AdminRouteTrackingPage />,
      },
      // {
      //   path: "analytics",
      //   element: <AnalyticsPage />,
      // },
      {
        path: "system",
        element: <SystemPage />,
      },
      {
        path: "demo-requests",
        element: <DemoRequestsPage />,
      },
      // {
      //   path: "barcodes",
      //   element: <BarCodePage />,
      // },
      {
        path: "settings",
        element: <SettingsPage />,
      },
    ],
  },
  // Pharmacy Routes - Completely separate from admin routes
  {
    path: "/pharmacy",
    element: (
      <ProtectedRoute requirePharmacyUser>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to="dashboard" replace />,
      },
      {
        path: "dashboard",
        element: <PharmacyDashboardPage />,
      },
      {
        path: "orders",
        element: <OrderManagementPage />,
      },
      {
        path: "pickup",
        element: <PickupPage />,
      },
      // {
      //   path: "delivery",
      //   element: <DeliveryPage />,
      // },
      {
        path: "analytics",
        element: <PharmacyAnalyticsPage />,
      },
      {
        path: "barcodes",
        element: <BarCodePage />,
      },
      // {
      //   path: "tracking",
      //   element: <PharmacyTrackingPage />,
      // },
      {
        path: "route-tracking",
        element: <PharmacyRouteTrackingPage />,
      },
      {
        path: "settings",
        element: <SettingsPage />,
      },
    ],
  },
  // Legacy route redirects for backward compatibility
  {
    path: "/dashboard",
    element: <Navigate to="/pharmacy/dashboard" replace />,
  },
  {
    path: "/dashboard/orders",
    element: <Navigate to="/pharmacy/dashboard" replace />,
  },
  {
    path: "/dashboard/settings",
    element: <Navigate to="/pharmacy/settings" replace />,
  },
  // Public Routes (no authentication required)
  {
    path: "/demo-request",
    element: <DemoRequestPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/signature/:token",
    element: <SignaturePadPage />,
    errorElement: <ErrorBoundary />,
  },
  // Catch-all route for 404 pages
  {
    path: "*",
    element: <NotFoundPage />,
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
